import { handleUpdatePatientReturn } from '@/entities/patient-return';
import { useSetState } from '@/shared/lib/customHooks';
import { showMessageError } from '@/shared/lib';
import _ from 'lodash';
import { useLayoutEffect } from 'react';
import type { ReturnStatusNotesProps } from './returnStatusNotes.types';

/**
 * Drives the return-status notes editor of a study, tracking the draft note against the value stored on the study's patientReturn record. Seeds state from patientReturn.notes, derives isChangeNote by comparing the draft with it, exposes reset, change and submit handlers, and re-runs the reset in a layout effect whenever the stored note changes.
 *
 * @param data - Study record whose patient-return notes are edited.
 * @param updateStudyDetail - Callback receiving the study merged with the updated patientReturn record.
 * @returns Notes state, the change flag and the reset, submit and text-change handlers.
 */
export const useReturnStatusNotes = ({ data, updateStudyDetail }: ReturnStatusNotesProps) => {
  const defaultNote = data?.patientReturn?.notes || '';

  const [state, setState] = useSetState<any>({
    notes: '',
    isSaving: false,
  });

  const isChangeNote = state.notes !== defaultNote;

  /**
   * Discards unsaved edits to a study's return-status notes by restoring the value held on its patientReturn record. Merges defaultNote back into state.notes.
   *
   * @returns Nothing.
   */
  const resetNotes = () => {
    setState({ notes: defaultNote });
  };

  /**
   * Persists the drafted return-status notes onto the study's patient-return record. Sends the record id and current notes through handleUpdatePatientReturn, merges the returned patientReturn into a deep clone of the study for updateStudyDetail, and on failure logs the error and shows an error message, always clearing isSaving.
   *
   * @returns Promise resolving once the update and the state cleanup finished.
   */
  const onSubmit = async () => {
    try {
      setState({ isSaving: true });
      const input = {
        id: data?.patientReturn?.id,
        notes: state.notes,
      };
      const patientReturn = await handleUpdatePatientReturn(input);
      const clonedData = _.cloneDeep(data);
      const updatedData = {
        ...clonedData,
        patientReturn,
      };
      updateStudyDetail(updatedData);
    } catch (error) {
      console.error('Failed to update patient return: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    } finally {
      setState({ isSaving: false });
    }
  };

  /**
   * Writes an edited return-status note value into the notes editor state under the field name of the textarea that produced it. The name argument doubles as the state key so setState merges only that field.
   *
   * @param name - Name of the input, used as the state key.
   * @param value - New text value stored for that field.
   * @returns Nothing.
   */
  const onChangeInputText = (name: any, value: any) => {
    setState({ [name]: value });
  };

  useLayoutEffect(() => {
    resetNotes();
  }, [defaultNote]);

  return { state, isChangeNote, resetNotes, onSubmit, onChangeInputText };
};
